import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Menu,
  Drawer,
  Avatar,
  Dropdown,
  Space,
  Button,
  Grid,
  Typography,
  theme,
} from 'antd';
import PageLoading from '../components/PageLoading';
import ChunkLoadErrorBoundary from '../components/ChunkLoadErrorBoundary';
import AppBrand from '../components/AppBrand';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  MenuOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import useUiStore from '../store/useUiStore';
import useAuthStore from '../store/useAuthStore';
import { layout } from '../utils/tokens';
import { NAV } from '../config/nav';
import {
  filterNav,
  selectedNavPath,
  submenuIdForPath,
  toAntdMenuItems,
} from '../utils/nav';

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;
const { Text } = Typography;

function displayName(user: ReturnType<typeof useAuthStore.getState>['user']): string {
  if (!user) return '用户';
  return (user.familyName && user.givenName ? `${user.familyName}${user.givenName}` : null) ||
    (user.lastName && user.firstName ? `${user.lastName}${user.firstName}` : null) ||
    user.email.split('@')[0] ||
    user.displayName ||
    '用户';
}

function avatarLabel(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '用';
  return trimmed.slice(-2);
}

const BasicLayout: React.FC = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { user } = useAuthStore();
  const { siderCollapsed, toggleSider, themeMode } = useUiStore();
  const menuTheme = themeMode === 'dark' ? 'dark' : 'light';
  const name = displayName(user);

  const visibleNav = useMemo(() => filterNav(NAV, user), [user]);
  const selectedKey = selectedNavPath(location.pathname, NAV);
  const activeSubmenuId = submenuIdForPath(location.pathname, NAV);

  const [menuOpenKeys, setMenuOpenKeys] = useState<string[]>(() =>
    activeSubmenuId ? [activeSubmenuId] : [],
  );

  useEffect(() => {
    if (activeSubmenuId) {
      setMenuOpenKeys((prev) => (prev.includes(activeSubmenuId) ? prev : [...prev, activeSubmenuId]));
    }
  }, [activeSubmenuId]);

  const desktopMenuItems = useMemo(
    () => toAntdMenuItems(visibleNav, { collapsed: siderCollapsed }),
    [visibleNav, siderCollapsed],
  );
  const drawerMenuItems = useMemo(
    () => toAntdMenuItems(visibleNav, { collapsed: false }),
    [visibleNav],
  );

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        window.location.href = '/auth/saml/logout';
      },
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key.startsWith('/')) {
      navigate(key);
      setDrawerVisible(false);
    }
  };

  const brandButton = (collapsed: boolean) => (
    <button
      type="button"
      onClick={() => {
        navigate('/dashboard');
        setDrawerVisible(false);
      }}
      aria-label="回到首页"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        width: '100%',
        height: layout.headerHeight,
        paddingInline: collapsed ? 0 : token.padding,
        border: 'none',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        background: 'transparent',
        cursor: 'pointer',
      }}
    >
      <AppBrand collapsed={collapsed} />
    </button>
  );

  const renderMenu = (items: MenuProps['items']) => (
    <Menu
      theme={menuTheme}
      mode="inline"
      selectedKeys={[selectedKey]}
      openKeys={menuOpenKeys}
      onOpenChange={setMenuOpenKeys}
      items={items}
      onClick={handleMenuClick}
      style={{ borderInlineEnd: 'none', paddingTop: token.paddingXS }}
    />
  );

  const siderWidth = siderCollapsed ? layout.siderCollapsedWidth : layout.siderWidth;

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      {screens.lg && (
        <Sider
          theme={menuTheme}
          trigger={null}
          collapsible
          collapsed={siderCollapsed}
          width={layout.siderWidth}
          collapsedWidth={layout.siderCollapsedWidth}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            insetInlineStart: 0,
            top: 0,
            bottom: 0,
            borderInlineEnd: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          {brandButton(siderCollapsed)}
          {renderMenu(desktopMenuItems)}
        </Sider>
      )}

      <Drawer
        title={brandButton(false)}
        placement="left"
        closable={false}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible && !screens.lg}
        styles={{
          header: { padding: 0 },
          body: { padding: 0 },
        }}
        size={layout.siderWidth}
      >
        {renderMenu(drawerMenuItems)}
      </Drawer>

      <Layout
        style={{
          marginInlineStart: screens.lg ? siderWidth : 0,
          transition: `margin-inline-start ${token.motionDurationMid}`,
          background: token.colorBgLayout,
        }}
      >
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingInline: token.paddingSM,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            position: 'sticky',
            top: 0,
            zIndex: 20,
            lineHeight: `${layout.headerHeight}px`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: token.marginXS }}>
            {screens.lg ? (
              <Button
                type="text"
                icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={toggleSider}
                aria-label={siderCollapsed ? '展开菜单' : '收起菜单'}
              />
            ) : (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                aria-label="打开菜单"
              />
            )}
            {!screens.lg && <AppBrand />}
          </div>

          <Space size={token.marginXS}>
            <Button
              type="text"
              href="https://apps.habby.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Habby Apps
              <ExportOutlined aria-hidden style={{ marginInlineStart: 4, fontSize: '0.85em' }} />
            </Button>

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
              <Button type="text" style={{ height: 40, paddingInline: token.paddingXS }}>
                <Space size={token.marginXS}>
                  <Avatar
                    size={28}
                    style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }}
                  >
                    {avatarLabel(name)}
                  </Avatar>
                  {screens.sm && (
                    <Text ellipsis style={{ maxWidth: 140 }}>
                      {name}
                    </Text>
                  )}
                </Space>
              </Button>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: token.marginLG,
            minHeight: 280,
          }}
        >
          <ChunkLoadErrorBoundary>
            <Suspense fallback={<PageLoading />}>
              <Outlet />
            </Suspense>
          </ChunkLoadErrorBoundary>
        </Content>
      </Layout>
    </Layout>
  );
};

export default BasicLayout;
